import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, Col, Row } from "react-bootstrap";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import Select from "react-select";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import Breadcrumb from "../../Breadcrumb/Breadcrumb";
import { BASEURL, MAP_KEY } from "../../../constant/api";
import useRequest from "../../../hooks/useRequest";

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "400px",
};
const center = {
  lat: 26.916804657627374,
  lng: 75.77984904488004,
};

const StepFive = ({
  outletId,
  allStepsData,
  setAllFormData,
  handleNextStep,
}) => {
  const history = useHistory();
  const token = useSelector((state) => state.auth?.token);
  const { response: responseCountry, request: requestCountry } = useRequest();
  const { response: responseState, request: requestState } = useRequest();
  const { response: responseCity, request: requestCity } = useRequest();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [countryID, setCountryId] = useState("");
  const [cityID, setCityId] = useState("");
  const [stateId, setStateID] = useState("");
  const [address, setAddress] = useState("");
  const [markerPosition, setMarkerPosition] = useState(center);
  const [latLong, setLatLong] = useState({
    latitude: "",
    longitude: "",
  });

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: MAP_KEY,
    libraries,
  });

  useEffect(() => {
    requestCountry("GET", "admin/getCountry");
  }, []);

  useEffect(() => {
    if (
      responseCountry?.status === "success" ||
      responseCountry?.status === true
    ) {
      setCountries(responseCountry?.data?.length ? responseCountry.data : []);
    }
  }, [responseCountry]);

  useEffect(() => {
    if (!allStepsData) {
      return;
    }
    setCountryId(allStepsData?.country || "");
    setStateID(allStepsData?.state || "");
    setCityId(allStepsData?.city || "");
    setAddress(allStepsData?.address || "");
    setLatLong({
      latitude: allStepsData?.latitude || "",
      longitude: allStepsData?.longitude || "",
    });
    if (allStepsData?.latitude && allStepsData?.longitude) {
      setMarkerPosition({
        lat: Number(allStepsData.latitude),
        lng: Number(allStepsData.longitude),
      });
    }
  }, [allStepsData]);

  useEffect(() => {
    if (countryID) {
      requestState("POST", "admin/getState", { countryId: countryID });
    }
  }, [countryID]);

  useEffect(() => {
    if (stateId) {
      requestCity("POST", "admin/vendor/getCityByState", { stateId });
    }
  }, [stateId]);

  useEffect(() => {
    if (responseState?.status === "success" || responseState?.status === true) {
      setStates(responseState?.data?.length ? responseState.data : []);
    }
  }, [responseState]);

  useEffect(() => {
    if (responseCity?.status === "success" || responseCity?.status === true) {
      setCities(responseCity?.data?.length ? responseCity.data : []);
    }
  }, [responseCity]);

  const initialValues = useMemo(
    () => ({
      country: allStepsData?.country || "",
      state: allStepsData?.state || "",
      city: allStepsData?.city || "",
      street: allStepsData?.street || "",
      landMark: allStepsData?.landMark || "",
      pincode: allStepsData?.pincode || "",
    }),
    [allStepsData],
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      country: Yup.string().required("Country is required"),
      state: Yup.string().required("State is required"),
      landMark: Yup.string().required("LandMark is required"),
      pincode: Yup.string().required("Postal Code is required"),
    }),
    onSubmit: async (values) => {
      if (!cityID) {
        toast.error("City is required");
        return;
      }

      const payload = {
        ...values,
        city: cityID,
        address,
        outletId,
        vendorId: allStepsData?.vendorId,
        latitude: latLong.latitude ? String(latLong.latitude) : "",
        longitude: latLong.longitude ? String(latLong.longitude) : "",
      };

      try {
        const response = await axios.post(
          `${BASEURL.PORT}/admin/vendor/updateStepFive`,
          payload,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        toast.success(
          response?.data?.message || "Outlet address updated successfully.",
        );
        setAllFormData((prev) => ({
          ...prev,
          ...payload,
        }));

        history.push(`/vendor/total-outlets/${allStepsData?.vendorId}`);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Unable to save outlet address.",
        );
      }
    },
  });

  const cityOptions = cities.map((city) => ({
    value: city?._id,
    label: city?.name,
  }));

  const handleSelect = async (value) => {
    const results = await geocodeByAddress(value);
    const latLng = await getLatLng(results[0]);
    setMarkerPosition(latLng);
    setLatLong({
      latitude: latLng.lat,
      longitude: latLng.lng,
    });
    setAddress(value);
  };

  const geocodeLatLng = (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      {
        location: {
          lat: parseFloat(lat),
          lng: parseFloat(lng),
        },
      },
      (results, status) => {
        if (status === "OK" && results?.[0]) {
          setAddress(results[0].formatted_address);
        }
      },
    );
  };

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading Maps...</div>;
  }

  return (
    <div className="ms-content-wrapper">
      <div className="row">
        <div className="col-md-12">
          <Breadcrumb
            title="Address"
            links={[
              { to: "/", name: "Home" },
              { to: "/vendors", name: "Outlets" },
            ]}
          />

          <div className="row">
            <div className="col-xl-12 col-md-12">
              <div className="ms-panel ms-panel-fh">
                <div className="ms-panel-header">
                  <h6>Address</h6>
                </div>
                <div className="ms-panel-body">
                  <form
                    className="needs-validation clearfix"
                    noValidate
                    onSubmit={formik.handleSubmit}
                  >
                    <div className="form-row">
                      <div className="col-md-12 mb-3">
                        <label htmlFor="country">
                          Country<span className="error">*</span>
                        </label>
                        <div className="input-group">
                          <select
                            className="form-control"
                            name="country"
                            value={formik.values.country}
                            onChange={(event) => {
                              formik.handleChange(event);
                              setCountryId(event.target.value);
                              setStateID("");
                              setCityId("");
                            }}
                          >
                            <option value="">Select Country</option>
                            {countries.map((country) => (
                              <option key={country._id} value={country._id}>
                                {country.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="state">
                          State<span className="error">*</span>
                        </label>
                        <div className="input-group">
                          <select
                            className="form-control capitalize"
                            name="state"
                            value={formik.values.state}
                            onChange={(event) => {
                              formik.handleChange(event);
                              setStateID(event.target.value);
                              setCityId("");
                            }}
                          >
                            <option value="">Select State</option>
                            {states.map((state) => (
                              <option key={state._id} value={state._id}>
                                {state.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="city">
                          City<span className="error">*</span>
                        </label>
                        <div className="input-group">
                          <Select
                            id="city"
                            name="city"
                            className="border-blue w-100"
                            options={cityOptions}
                            onChange={({ value }) => setCityId(value)}
                            value={cityOptions.filter(
                              ({ value }) => value === cityID,
                            )}
                            placeholder="Select City"
                          />
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="street">
                          Location / Area <span className="error">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="street"
                            value={formik.values.street}
                            onChange={formik.handleChange}
                            id="street"
                            placeholder="Street"
                          />
                        </div>
                      </div>

                      <div className="col-md-6 mb-3 ">
                        <label htmlFor="address">
                          Address<span className="error">*</span>
                        </label>
                        <div className="mb-3">
                          <PlacesAutocomplete
                            value={address}
                            onChange={setAddress}
                            onSelect={handleSelect}
                          >
                            {({
                              getInputProps,
                              suggestions,
                              getSuggestionItemProps,
                              loading,
                            }) => (
                              <div>
                                <input
                                  {...getInputProps({
                                    placeholder: "Search Places ...",
                                    className: "form-control",
                                  })}
                                />
                                <div className="autocomplete-dropdown-container">
                                  {loading && <div>Loading...</div>}
                                  {suggestions.map((suggestion) => (
                                    <div
                                      key={suggestion.placeId}
                                      {...getSuggestionItemProps(suggestion, {
                                        className: suggestion.active
                                          ? "suggestion-item--active"
                                          : "suggestion-item",
                                        style: {
                                          backgroundColor: suggestion.active
                                            ? "#fafafa"
                                            : "#ffffff",
                                          cursor: "pointer",
                                        },
                                      })}
                                    >
                                      <span>{suggestion.description}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </PlacesAutocomplete>
                        </div>
                      </div>

                      <div className="col-md-12 mb-3 ">
                        <GoogleMap
                          mapContainerStyle={mapContainerStyle}
                          zoom={10}
                          center={markerPosition}
                          onClick={(event) => {
                            geocodeLatLng(
                              event.latLng.lat(),
                              event.latLng.lng(),
                            );
                            setMarkerPosition({
                              lat: event.latLng.lat(),
                              lng: event.latLng.lng(),
                            });
                            setLatLong({
                              latitude: event.latLng.lat(),
                              longitude: event.latLng.lng(),
                            });
                          }}
                        >
                          <Marker position={markerPosition} />
                        </GoogleMap>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="landMark">
                          Nearest Land Mark<span className="error">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="landMark"
                            value={formik.values.landMark}
                            onChange={formik.handleChange}
                            id="landMark"
                            placeholder="Land Mark"
                          />
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label htmlFor="pincode">
                          Postal code<span className="error">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            name="pincode"
                            value={formik.values.pincode}
                            onChange={formik.handleChange}
                            id="pincode"
                            placeholder="Postal code"
                          />
                        </div>
                      </div>

                      {latLong.latitude && latLong.longitude && (
                        <>
                          <div className="col-md-6 mb-3">
                            <label htmlFor="latitude">Latitude</label>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                value={latLong.latitude}
                                id="latitude"
                                disabled
                              />
                            </div>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label htmlFor="longitude">Longitude</label>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                value={latLong.longitude}
                                id="longitude"
                                disabled
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="row mt-4">
                      <div className="col-md-6">
                        <button
                          className="steps-btn float-left"
                          type="button"
                          onClick={() => handleNextStep(4)}
                        >
                          <i>&#8592;</i> Previous
                        </button>
                      </div>
                      <div className="col-md-6">
                        <button className="steps-btn float-right" type="submit">
                          Save
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepFive;
